import React, { useState } from 'react';
import Button from 'components/ui/Button';
import { ExternalLink, Copy, BarChart3, Calendar, TrendingUp, TrendingDown } from 'lucide-react';

const LinkPerformanceTable = ({ trackingLinks, clickData }) => {
  const [sortField, setSortField] = useState('total_clicks');
  const [sortDirection, setSortDirection] = useState('desc');

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard?.writeText(text);
      // Could add toast notification here
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Actif';
      case 'paused':
        return 'En pause';
      case 'expired':
        return 'Expiré';
      case 'draft':
        return 'Brouillon';
      default:
        return status;
    }
  };

  const calculateCTR = (uniqueClicks, totalClicks) => {
    if (!totalClicks || totalClicks === 0) return 0;
    return ((uniqueClicks / totalClicks) * 100)?.toFixed(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedLinks = [...(trackingLinks || [])]?.sort((a, b) => {
    const aValue = a?.[sortField] || 0;
    const bValue = b?.[sortField] || 0;
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return null;
    }
    return sortDirection === 'asc' 
      ? <TrendingUp className="h-4 w-4" />
      : <TrendingDown className="h-4 w-4" />;
  };

  if (!trackingLinks || trackingLinks?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center py-8">
          <BarChart3 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            Aucun lien à analyser
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            Créez vos premiers liens de suivi pour voir les performances ici
          </p>
          <Button 
            onClick={() => window.location.href = '/link-generator'}
            className="mt-4"
          >
            Créer un lien
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">
          Performance des Liens
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Détails de performance pour chaque lien de suivi
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('title')}
              >
                <div className="flex items-center space-x-1">
                  <span>Lien</span>
                  {getSortIcon('title')}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('total_clicks')}
              >
                <div className="flex items-center space-x-1">
                  <span>Clics Totaux</span>
                  {getSortIcon('total_clicks')}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('unique_clicks')}
              >
                <div className="flex items-center space-x-1">
                  <span>Clics Uniques</span>
                  {getSortIcon('unique_clicks')}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                CTR
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('created_at')}
              >
                <div className="flex items-center space-x-1">
                  <span>Créé le</span>
                  {getSortIcon('created_at')}
                </div>
              </th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedLinks?.map((link) => (
              <tr key={link?.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col">
                    <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                      {link?.title}
                    </div>
                    {link?.campaigns && (
                      <div className="text-xs text-indigo-600">
                        {link?.campaigns?.name}
                      </div>
                    )}
                    {link?.utm_source && (
                      <div className="text-xs text-gray-500">
                        Source: {link?.utm_source}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {link?.total_clicks || 0}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {link?.unique_clicks || 0}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {calculateCTR(link?.unique_clicks, link?.total_clicks)}%
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(link?.status)}`}>
                    {getStatusText(link?.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    {formatDate(link?.created_at)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(link?.short_url || '')}
                      title="Copier le lien"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(link?.short_url, '_blank')}
                      title="Ouvrir le lien"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Table Footer with Summary */}
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>
            Total: {trackingLinks?.length || 0} lien(s)
          </span>
          <div className="flex items-center space-x-4">
            <span>
              {trackingLinks?.reduce((sum, link) => sum + (link?.total_clicks || 0), 0) || 0} clics totaux
            </span>
            <span>
              {trackingLinks?.reduce((sum, link) => sum + (link?.unique_clicks || 0), 0) || 0} clics uniques
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkPerformanceTable;